"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, MessageSquare, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const trustIndicators = [{
  text: "End-to-end encryption"
}, {
  text: "Self-hosted option available"
}];
const checklistFeatures = [{
  text: "Group and one-on-one messaging"
}, {
  text: "Voice and video calling"
}, {
  text: "File sharing and screen sharing"
}];
export default function TroopMessenger() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="troopmessengerpromo" data-nav="dark" className="relative overflow-hidden bg-[hsl(221,40%,4%)] py-20 text-white lg:py-32">
      {/* Decorative background numeral */}
      <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-4 select-none font-mono text-[200px] font-bold text-white/5 sm:left-10 md:text-[280px]">
        05
      </span>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Left Column */}
          <div className="flex flex-col lg:col-span-5">
            <motion.div variants={itemVariants} className="flex items-center gap-3">
              <span className="h-4 w-1 rounded-full bg-primary" />
              <span className="font-mono text-4xl font-semibold tracking-widest text-primary uppercase sm:text-5xl lg:text-6xl">
                05 // Troop Messenger
              </span>
            </motion.div>

            <motion.h2 variants={itemVariants} className="mt-4 font-default text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Secure team communication, simplified
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-6 text-base leading-relaxed text-white/75 sm:text-lg">
              Troop Messenger brings chat, audio, video calling, and asset sharing into one secure workplace hub. Built for privacy-first teams who need control over their operational data.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap gap-3">
              {trustIndicators.map((item, i) => <div key={item.text} data-index={i} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-white/90 backdrop-blur-xs">
                  <ShieldCheck className="size-4 text-primary" />
                  <span>{item.text}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Right Column / Featured Card */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <div className="relative">
              {/* L-shaped corner accent brackets in primary color */}
              <div aria-hidden="true" className="pointer-events-none absolute -top-2 -left-2 z-10 size-5 border-t-2 border-l-2 border-primary" />
              <div aria-hidden="true" className="pointer-events-none absolute -top-2 -right-2 z-10 size-5 border-t-2 border-r-2 border-primary" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-2 -left-2 z-10 size-5 border-b-2 border-l-2 border-primary" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-2 -right-2 z-10 size-5 border-b-2 border-r-2 border-primary" />

              <Card className="relative rounded-lg border border-[hsl(221,30%,15%)] bg-[hsl(221,30%,8%)] p-6 text-white shadow-2xl md:p-12">
                <div className="flex flex-col gap-6">
                  {/* Top Bar with Icon and Badge */}
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex size-14 items-center justify-center rounded-lg bg-primary/10 text-primary md:size-16">
                      <MessageSquare className="size-7 md:size-8" />
                    </div>
                    <Badge variant="outline" className="border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold tracking-wider text-primary uppercase">
                      Best for team collaboration
                    </Badge>
                  </div>

                  {/* Card Title & Description */}
                  <div>
                    <h3 className="font-default text-2xl font-bold text-white md:text-3xl">
                      Enterprise Team Chat
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">
                      Real-time group collaboration, encrypted 1:1 threads, and rapid file exchanges configured to run either on the cloud or private enterprise hosting.
                    </p>
                  </div>

                  <Separator className="bg-white/10" />

                  {/* Feature Checklist */}
                  <ul className="space-y-3.5">
                    {checklistFeatures.map((feature, i) => <li key={feature.text} data-index={i} className="flex items-center gap-3 text-sm font-medium text-white/90 md:text-base">
                        <CheckCircle2 className="size-5 shrink-0 text-primary" />
                        <span>{feature.text}</span>
                      </li>)}
                  </ul>

                  {/* Action CTA */}
                  <div className="pt-2">
                    <Button asChild className="h-14 w-full rounded-md bg-primary text-base font-semibold text-primary-foreground shadow-lg transition-colors hover:bg-primary/90">
                      <Link to="https://vpngenix.com/go/TroopMessenger" newTab className="flex items-center justify-center gap-2">
                        <span>Visit Site</span>
                        <ArrowRight className="size-5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}